// src/api.js
import ReactGA from "react-ga4";
import { hasAnalyticsConsent } from "./consent";
import { localizedHref } from "./utils/localizedNav";

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// --- GA Event Helper (only fires if consent given) ---
export function trackEvent(action, category = "engagement", label = "", value) {
  if (hasAnalyticsConsent() && window.__gaInitialized) {
    ReactGA.event({ category, action, label, ...(value !== undefined && { value }) });
  }
}

// --- JWT Token Helpers ---

export function getToken() {
  return localStorage.getItem('token');
}

export function setToken(token) {
  localStorage.setItem('token', token);
  window.dispatchEvent(new Event('aof-auth-changed'));
}

export function clearToken() {
  localStorage.removeItem('token');
  window.dispatchEvent(new Event('aof-auth-changed'));
}

export function isAuthenticated() {
  return !!getToken();
}

// --- Central auth-state cleanup ---
// Every localStorage key that represents user auth/session state. Kept as a
// single list so logout — wherever it's triggered from — always clears the
// same keys. adminToken is intentionally excluded: the admin panel has its
// own, unrelated login flow.
const AUTH_STORAGE_KEYS = [
  'token', 'userId', 'username', 'email',
  'isModerator', 'modPermissions', 'waveAccess', 'unreadNotifications',
  // Von UserLogin.js gesetzte Profil-/Gamification-Werte — sonst sieht auf einem
  // geteilten Gerät der nächste User Premium-Status/XP/Badges der vorherigen Person.
  'xp', 'level', 'badges', 'achievements', 'isPremium', 'loginStreak', 'dailyBonusJustEarned',
  'aof-diary-consent',
];

export function clearAllAuthKeys() {
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  window.dispatchEvent(new Event('aof-auth-changed'));
}

// Build Authorization header
export function authHeaders(extraHeaders = {}) {
  const token = getToken();
  return {
    ...extraHeaders,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// Liest das exp-Feld (Sekunden) aus dem JWT-Payload; null wenn nicht lesbar.
export function getTokenExpiry(token = getToken()) {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof json.exp === 'number' ? json.exp : null;
  } catch {
    return null;
  }
}

export function isTokenExpired(token = getToken()) {
  const exp = getTokenExpiry(token);
  return exp !== null && exp * 1000 <= Date.now();
}

// Abgelaufenen Token schon beim App-Start still entfernen — sonst würde der erste
// Hintergrund-Request (z. B. CountryNudgeBanner auf jeder Seite) per 401 mitten im
// Lesen auf /login umleiten.
if (typeof window !== 'undefined' && isTokenExpired()) {
  clearAllAuthKeys();
}

// Authenticated fetch wrapper — auto-attaches JWT, auto-logout on 401.
// Umleitung auf /login NUR bei Aktionen des Users (POST/PATCH/PUT/DELETE). Reine
// Lese-Requests (GET) laufen oft im Hintergrund — dort wird nur still ausgeloggt.
export async function authFetch(url, options = {}) {
  if (isTokenExpired()) clearAllAuthKeys();
  const headers = authHeaders(options.headers || {});
  const res = await fetch(url, { ...options, headers });

  if (res.status === 401) {
    clearAllAuthKeys();
    const method = (options.method || 'GET').toUpperCase();
    if (method !== 'GET' && method !== 'HEAD') {
      window.location.href = localizedHref('/login');
    }
    throw new Error('Session expired');
  }

  return res;
}
